import { noop } from "lodash-es";
import type { Ref } from "vue";
import { onMounted } from "vue";

import { useNativeEvents } from "@/composables/useNativeEvents";
import { scrollTo } from "@/math/coordinate-utils";
import { applyCoord } from "@/math/coordinates";
import type { WindowCoordinate } from "@/model/coordinates";
import { clientCoord, windowFromViewport } from "@/utils/dom/coordinates";
import { releaseCapture } from "@/utils/dom/dom";

import { isDragging } from "./drag";
import { isPinching } from "./pinch";

let dragScrolling = false;
let onDragScrollEnd = noop;

export function executeAfterDragScroll(action: () => void) {
  if (dragScrolling) {
    onDragScrollEnd = action;
  } else {
    action();
  }
}

export function useDragScroll(el: Ref<HTMLElement | SVGSVGElement | null>) {
  const { addEventListener } = useNativeEvents();

  let disabled = false;
  let ev: HTMLElement;
  let pos: WindowCoordinate;

  onMounted(() => {
    // ts is not clever enough to see that HTMLElement and SVGSVGElement support the same EventListeners
    // assume el is not null in onMounted
    ev = el.value as HTMLElement;

    addEventListener(ev, "pointerdown", start);
    addEventListener(ev, "pointermove", move);
    addEventListener(ev, "pointerenter", end);
  });

  /**
   * Reenable drag-based scrolling.
   *
   * @param immediate - If `true`, starts scrolling immediately when the function is called
   *                    instead of waiting for the next `pointerdown` event.
   *
   * - Use `enableDragScroll()` to reenable standard drag scrolling (after disableDragScroll() has been called).
   * - Use `enableDragScroll(true)` when calling from an existing `pointerdown` handler
   *   to immediately initiate scrolling without waiting for another event.
   *   When `dragScroll` is triggered inside a `pointerdown` event (e.g., right-click),
   *   the listeners are registered *after* the event has already occurred. This means
   *   that `pointerdown` won't fire again until the user clicks again. The `immediate` flag
   *   ensures that scrolling starts immediately by manually triggering `start`
   *   on the first `pointermove` event.
   */
  function enable(immediate?: boolean) {
    disabled = false;
    if (immediate) {
      ev.addEventListener("pointermove", start, { once: true });
    }
  }

  function disable() {
    disabled = true;
  }

  return { enableDragScroll: enable, disableDragScroll: disable };

  function start(e: PointerEvent) {
    if (!disabled) {
      releaseCapture(e);
      dragScrolling = true;
      setPos(e);
      ev.addEventListener("pointerup", end, { once: true });
    }
  }

  function move(e: PointerEvent) {
    if (!disabled && dragScrolling && !isPinching() && !isDragging()) {
      ev.style.cursor = "grabbing";
      scrollTo(pos, truncCoord(e));
      setPos(e);
    }
  }

  function end() {
    dragScrolling = false;
    onDragScrollEnd();
    onDragScrollEnd = noop;
    ev.style.removeProperty("cursor");
  }

  function setPos(e: PointerEvent) {
    pos = windowFromViewport(truncCoord(e));
  }

  function truncCoord(e: PointerEvent) {
    return applyCoord(clientCoord(e), Math.trunc);
  }
}
